import React, {useCallback, useMemo, useState} from 'react';
import {useLocalization} from 'localization';
import {noOp} from 'services/utils';
import {DialogItems} from 'components/dialog/DialogItems';
import type {DialogActionItem} from '../dialog/types';
import type {WizardStepProps} from './types';
import {FlexBox, Layout} from '../layout';
import {Spacer} from '../spacer';
import {WizardHeader} from './components/WizardHeader';

type WizardProps = {
  title: string;
  initialStepId?: string;
  steps: WizardStepProps[];
  // Show save if there are changes, or if new (with data from noraf)
  showSave?: boolean;
  // Show cancel if there are changes
  showCancel?: boolean;
  /** Show button to navigate to previous step */
  showPrev?: boolean;
  validateStep?: (stepIndex: number) => boolean;
  onGotoStep?: (stepId: string) => void;
  onCancel?: () => void;
  onSave: () => Promise<void> | void;
};

const getStepIndex = (
  stepId: string | undefined,
  steps: WizardStepProps[],
): number | undefined => {
  const index = stepId ? steps.findIndex(s => s.id === stepId) : -1;
  return index >= 0 ? index : undefined;
};

export const Wizard: React.FC<WizardProps> = ({
  title,
  initialStepId,
  steps: wizardSteps,
  showSave,
  showCancel,
  showPrev,
  validateStep,
  onGotoStep,
  onCancel,
  onSave,
}) => {
  const {t} = useLocalization();
  const [activeStep, setActiveStep] = useState(
    getStepIndex(initialStepId, wizardSteps) ?? 0,
  );

  const handleStepSelected = useCallback(
    (stepIdx: number) => {
      onGotoStep?.(wizardSteps[stepIdx].id);
      setActiveStep(stepIdx);
    },
    [onGotoStep, wizardSteps],
  );

  const {actions, steps} = useMemo(() => {
    const cancelAction: DialogActionItem = {
      itemType: 'action',
      title: t('general.cancel'),
      color: 'secondary',
      hide: !showCancel || !onCancel,
      onClick: onCancel ?? noOp,
    };
    const saveAction: DialogActionItem | undefined =
      activeStep === wizardSteps.length - 1
        ? {
            itemType: 'action',
            title: t('general.save'),
            color: 'success',
            hide: !showSave,
            icon: 'Check',
            showSpinnerOnClick: true,
            onClick: onSave,
          }
        : undefined;
    const prevAction: DialogActionItem | undefined =
      activeStep > 0 && showPrev
        ? {
            itemType: 'action',
            title: wizardSteps[activeStep - 1].label,
            icon: 'Back',
            variant: 'text',
            onClick: () => handleStepSelected(activeStep - 1),
          }
        : undefined;

    const nextAction: DialogActionItem | undefined =
      activeStep < wizardSteps.length - 1
        ? {
            itemType: 'action',
            title: wizardSteps[activeStep + 1].label,
            icon: 'ArrowRight',
            // Next disabled if current is invalid, default enabled
            disabled: validateStep ? !validateStep(activeStep) : false,
            onClick: () => handleStepSelected(activeStep + 1),
          }
        : undefined;

    return {
      actions: [
        prevAction,
        wizardSteps[activeStep].extraitem,
        cancelAction,
        saveAction,
        nextAction,
      ].filter(a => !!a) as DialogActionItem[],
      steps: wizardSteps.map(({extraitem: _, ...stepProps}) => stepProps),
    };
  }, [
    activeStep,
    handleStepSelected,
    onCancel,
    onSave,
    showCancel,
    showPrev,
    showSave,
    t,
    validateStep,
    wizardSteps,
  ]);

  return (
    <FlexBox>
      <DialogItems dialogItems={actions} />
      <WizardHeader
        title={title}
        showStepNumbers={false}
        activeStep={activeStep}
        validateStep={validateStep}
        onStepSelected={handleStepSelected}
        steps={steps}
      />
      <Spacer size={4} />
      <Layout>{steps[activeStep].content}</Layout>
    </FlexBox>
  );
};
